// Extra design axes for the dev playground: type, density (spacing) and radius.
// Every option is a small set of CSS custom-property overrides written onto
// <html> — the same live-override mechanism the color presets use. All fonts
// here ship a full Vietnamese subset (verified against fonts.googleapis.com).

export interface FontPreset {
  id: string;
  name: string;
  vibe: string;
  /** Body / UI stack -> --font-sans */
  sans: string;
  /** Display / heading stack -> --font-display */
  display: string;
}

export interface ScaleOption {
  id: string;
  name: string;
  /** short helper line */
  hint: string;
  /** CSS value written to the target token */
  value: string;
}

// Google Fonts to lazy-load once (dev only). Every family below ships a full
// Vietnamese subset (verified against fonts.googleapis.com with a modern UA).
// NOTE: the shipped default (Bricolage + Hanken + Geist Mono) is loaded from
// index.html; the rest are loaded here on first non-default pick. Be Vietnam Pro
// USED to ship from index.html but no longer does (typeset migration retired it
// as the primary face), so it MUST be listed here or the "Be Vietnam Pro" preset
// renders nothing.
export const DEV_FONT_HREF =
  'https://fonts.googleapis.com/css2?' +
  [
    'family=Be+Vietnam+Pro:wght@300;400;500;600;700',
    'family=Bricolage+Grotesque:opsz,wght@12..96,400..700',
    'family=Hanken+Grotesk:wght@400;500;600;700',
    'family=Familjen+Grotesk:wght@400;500;600;700',
    'family=Lexend:wght@400;500;600;700',
    'family=Public+Sans:wght@400;500;600;700',
    'family=Geist:wght@400;500;600;700',
    'family=Manrope:wght@400;500;600;700',
    'family=Geist+Mono:wght@400;500',
  ].join('&') +
  '&display=swap';

export const fontPresets: FontPreset[] = [
  {
    id: 'bricolage-hanken',
    name: 'Bricolage · Hanken',
    vibe: 'Ledger tinh tế — mặc định',
    sans: '"Hanken Grotesk", system-ui, sans-serif',
    display: '"Bricolage Grotesque", "Hanken Grotesk", system-ui, sans-serif',
  },
  {
    id: 'be-vietnam',
    name: 'Be Vietnam Pro',
    vibe: 'Việt hoá gốc, ấm',
    sans: '"Be Vietnam Pro", system-ui, sans-serif',
    display: '"Be Vietnam Pro", system-ui, sans-serif',
  },
  {
    id: 'geist',
    name: 'Geist',
    vibe: 'Chính xác, hiện đại',
    sans: '"Geist", system-ui, sans-serif',
    display: '"Geist", system-ui, sans-serif',
  },
  {
    id: 'familjen-lexend',
    name: 'Familjen · Lexend',
    vibe: 'Hình học, gần gũi',
    sans: '"Lexend", system-ui, sans-serif',
    display: '"Familjen Grotesk", "Lexend", system-ui, sans-serif',
  },
  {
    id: 'public-hanken',
    name: 'Public Sans · Hanken',
    vibe: 'Trung tính, rành mạch',
    sans: '"Hanken Grotesk", system-ui, sans-serif',
    display: '"Public Sans", "Hanken Grotesk", system-ui, sans-serif',
  },
  {
    id: 'bricolage-manrope',
    name: 'Bricolage · Manrope',
    vibe: 'Ledger + thân thiện',
    sans: '"Manrope", system-ui, sans-serif',
    display: '"Bricolage Grotesque", "Manrope", system-ui, sans-serif',
  },
];

// Rescales Tailwind v4's global --spacing base. Every spacing/size utility
// derives from it (calc(--spacing * n)), so this is a true density control.
// 'normal' is DEFAULT_SPACING_ID and must mirror the SHIPPED --spacing in
// global.css (0.2rem) so picking it (removeProperty) lands on the same value.
export const spacingOptions: ScaleOption[] = [
  { id: 'compact', name: 'Gọn', hint: 'Rất dày', value: '0.17rem' },
  { id: 'normal', name: 'Chuẩn', hint: 'Mặc định', value: '0.2rem' },
  { id: 'relaxed', name: 'Thoáng', hint: 'Dễ thở', value: '0.24rem' },
  { id: 'spacious', name: 'Rộng', hint: 'Rất thoáng', value: '0.28rem' },
];

// Overrides --radius; --radius-sm/md/lg derive from it via calc() in global.css.
// NOTE: 'soft' is DEFAULT_RADIUS_ID, so picking it calls removeProperty and
// falls back to the SHIPPED --radius in global.css (0.75rem / 12px). Its value
// here must mirror that shipped value. Spread: 0 / 4 / 12(default) / 16 px.
export const radiusOptions: ScaleOption[] = [
  { id: 'sharp', name: 'Vuông', hint: 'Góc vuông', value: '0rem' },
  { id: 'ledger', name: 'Ledger', hint: 'Sắc · 4px', value: '0.25rem' },
  { id: 'soft', name: 'Mềm', hint: 'Mặc định · 12px', value: '0.75rem' },
  { id: 'rounded', name: 'Tròn', hint: 'Bo rõ · 16px', value: '1rem' },
];

export const DEFAULT_FONT_ID = 'bricolage-hanken';
export const DEFAULT_SPACING_ID = 'normal';
export const DEFAULT_RADIUS_ID = 'soft';
